import { createSelector } from "reselect";
import get from "lodash/get";
import find from "lodash/find";

import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import {
  personalDetailsSelector,
  selectedNumberSelector,
  formValuesSelector
} from "./index";
import { orderSelector } from "./order";
import {
  ERROR_CODES,
  forms,
  SIM_TYPES,
  CONTRACT_TYPES,
  SHIPMENT_METHOD,
  E_SIM_DELIVERY_CONFIG_TYPE
} from "utils/constants";

const eSimDeliveryConfig = state =>
  get(state, "api.eSimDeliveryConfig.data", {});

export const eSimDeliverySpecificationsSelector = createSelector(
  eSimDeliveryConfig,
  deliveryConfig => {
    return get(deliveryConfig, "deliverySpecifications", []);
  }
);

export const newESimDeliverySelector = createSelector(
  eSimDeliverySpecificationsSelector,
  deliverySpecifications => {
    const newESimDelivery = find(deliverySpecifications, [
      "orderType",
      E_SIM_DELIVERY_CONFIG_TYPE.new
    ]);
    return newESimDelivery?.deliveryNeeded === true;
  }
);

export const numberAndDeliverySlotsAvailabilityDomain = state =>
  get(state, "api.numberAndDeliverySlotsAvailability", {});

export const checkNumberAndDeliverySlotsAvailabilitySelector = createSelector(
  numberAndDeliverySlotsAvailabilityDomain,
  numberAndDeliverySlotsAvailbility =>
    get(numberAndDeliverySlotsAvailbility, "data")
);

const personDetailsSelector = formValuesSelector(forms.PERSONAL_DETAILS_FORM);
const connectionDetailsSelector = formValuesSelector(
  forms.CONNECTION_DETAILS_FORM
);

export const checkNumberAndDeliverySlotsAvailabilityParamsSelector = createSelector(
  personalDetailsSelector,
  selectedNumberSelector,
  personDetailsSelector,
  connectionDetailsSelector,
  newESimDeliverySelector,
  (
    personalDetails,
    selectedNumber,
    personalDetailsValues,
    connectionDetailsValues,
    isAddESimDeliveryNeeded
  ) => {
    const { shippingMethod } = personalDetailsValues;
    const { number_type, sim_type } = connectionDetailsValues;

    return {
      params: JSON.stringify({
        ...(get(selectedNumber, "number") &&
          number_type === CONTRACT_TYPES.NEW && {
            number: {
              value: get(selectedNumber, "number")
            }
          }),
        ...(get(personalDetails, "delivery_date") &&
          get(personalDetails, "delivery_slot_id") &&
          get(shippingMethod, "name") === SHIPMENT_METHOD.DOORSTEP_DELIVERY &&
          (sim_type === SIM_TYPES.PHYSICAL_SIM || isAddESimDeliveryNeeded) && {
            slot: {
              value: get(personalDetails, "delivery_slot_id"),
              date: get(personalDetails, "delivery_date")
                .split("-")
                .join("/")
            }
          }),
        ...(get(personalDetails, "selfPickUpLocation.id") &&
          get(shippingMethod, "name") === SHIPMENT_METHOD.SELF_PICKUP &&
          (sim_type === SIM_TYPES.PHYSICAL_SIM || isAddESimDeliveryNeeded) && {
            pickup_location: {
              value: get(personalDetails, "selfPickUpLocation.id")
            }
          })
      })
    };
  }
);

const numberAndDeliverySlotErrorMapping = [
  {
    name: "number",
    selfError: "ERROR_NUMBER_NOT_AVAILEBLE"
  },
  {
    name: "slot",
    selfError: "ERROR_SLOT_NOT_AVAILEBLE",
    groupError: "ERROR_NUMBER_AND_SLOT_NOT_AVAILEBLE"
  },
  {
    name: "pickup_location",
    selfError: "ERROR_LOCATION_NOT_AVAILEBLE",
    groupError: "ERROR_NUMBER_AND_LOCATION_NOT_AVAILEBLE"
  }
];

export const numberAndDeliverySlotErrorCodeSelector = createSelector(
  orderSelector,
  checkNumberAndDeliverySlotsAvailabilitySelector,
  (order, numberAndDeliverySlotAvailability) => {
    let errorCode = "";
    // retrieve errorCode from availability check if order has not yet been created
    if (Object.keys(order).length === 1 && isEmpty(order.error)) {
      map(numberAndDeliverySlotErrorMapping, error => {
        if (
          !isEmpty(numberAndDeliverySlotAvailability) &&
          !isEmpty(get(numberAndDeliverySlotAvailability, `${error.name}`)) &&
          !get(
            get(numberAndDeliverySlotAvailability, `${error.name}`),
            "available"
          )
        ) {
          if (error.groupError && !isEmpty(errorCode)) {
            errorCode = error.groupError;
          } else {
            errorCode = error.selfError;
          }
        }
      });
    } else {
      // retrieve error code from order if order has already been created
      const orderErrorId = get(order, "errorId");
      const orderErrorCode = get(order, "errorCode");
      if (
        orderErrorId ===
          get(ERROR_CODES, "numberAndDeliverySlotAvailability") &&
        orderErrorCode
      )
        errorCode = orderErrorCode;
    }

    return errorCode;
  }
);

export const makeSelectNumberAndDeliverySlotsAvailabilityParams = () =>
  checkNumberAndDeliverySlotsAvailabilityParamsSelector;

export const makeSelectNumberAndDeliverySlotErrorCode = () =>
  numberAndDeliverySlotErrorCodeSelector;
